import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { organizationQuery } from '@/api/queries';
import type { PresetOut } from '@/api/types';
import { paths } from '@/app/paths';
import { useSession } from '@/app/session';
import { Button, Chip, Icon, PALETTES, resolvePalette, SearchInput, Segmented, ThumbPaletteProvider, useToast } from '@/design-system';
import { useBreadcrumbs } from '@/shell/breadcrumbs';
import { errorText } from '@/shell/errorText';
import { LoadError } from '@/shell/LoadError';
import { websiteQuery } from '@/features/sites/api';
import { PageHeader } from '@/shell/PageHeader';
import { demoCountQuery, patchCachedPreset, setFavorite, useLibraryList, type GalleryFilters } from './api';
import { usePresetLabels } from './labels';
import { NewPresetModal } from './NewPresetModal';
import { PresetCard } from './PresetCard';
import s from './library.module.css';

/** The design's segmented filter (libFilters): all, favourites, then six section types. */
const FILTERS = ['all', 'fav', 'hero', 'categories', 'featured', 'reviews', 'faq', 'contact'] as const;
type Filter = (typeof FILTERS)[number];

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

/**
 * /library (DESIGN_SPEC §4.7, P3-E02, P3-E03): Admin and PM; a PM gets published presets only (the API)
 * and no «Новый пресет» (the modal, P3-E03). A card's name opens the preset detail. Opened from a site's Structure tab (`?site=…&section=…`), «Добавить в структуру»
 * pins the preset's latest version into that section; otherwise it says how to get there.
 */
export function LibraryPage() {
  const { t } = useTranslation('library');
  const { t: tc } = useTranslation('common');
  const { isAdmin } = useSession();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const siteId = params.get('site');
  const sectionKey = params.get('section');
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [demo, setDemo] = useState(false);
  const [creating, setCreating] = useState(false);
  const search = useDebounced(query.trim(), 250);
  const labels = usePresetLabels();
  useBreadcrumbs([{ label: t('page.title') }]);

  const filters: GalleryFilters = useMemo(
    () => ({ ...(filter === 'fav' ? { favorites: true } : filter !== 'all' ? { section_type: filter } : {}), ...(search ? { search } : {}) }),
    [filter, search],
  );
  const list = useLibraryList(filters, demo);
  const demoCount = useQuery(demoCountQuery).data;

  // The site context: its name for the context line, its organization's brand palette for the thumbs.
  const site = useQuery({ ...websiteQuery(siteId ?? ''), enabled: !!siteId }).data;
  const org = useQuery({ ...organizationQuery(site?.organization.id ?? ''), enabled: !!site }).data;
  const palette = useMemo(() => (org ? resolvePalette('brand', org.brand_colors) : PALETTES.brand), [org]);

  const favorite = useMutation({
    mutationFn: (p: PresetOut) => setFavorite(p.id, !p.is_favorite),
    onMutate: (p) => patchCachedPreset(qc, p.id, { is_favorite: !p.is_favorite }),
    onError: (err, p) => {
      patchCachedPreset(qc, p.id, { is_favorite: p.is_favorite });
      toast(errorText(err, tc));
    },
    // Under «Избранное» the list itself changes: refetch it.
    onSettled: () => void (filter === 'fav' && qc.invalidateQueries({ queryKey: ['presets'] })),
  });

  const addHint = (p: PresetOut): string | undefined => {
    if (!siteId || !sectionKey) return t('card.add_hint');
    if (p.section_type !== sectionKey) return t('card.add_other_type', { section: labels.section(p.section_type) });
    if (!p.latest_version_id) return t('card.add_no_version');
    return undefined;
  };
  const add = (p: PresetOut) => {
    const hint = addHint(p);
    if (hint) return toast(hint);
    // The Structure tab (P4-D02) applies the pin (section ← this preset's latest version, TZ §7.2) and
    // confirms it with «… добавлен в структуру — версия зафиксирована» once the server has saved it.
    navigate(`${paths.site(siteId as string, 'structure')}?${new URLSearchParams({ section: sectionKey as string, pin: p.latest_version_id as string })}`);
  };

  return (
    <>
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        row
        action={
          isAdmin && (
            <Button icon="plus" onClick={() => setCreating(true)}>
              {t('page.new')}
            </Button>
          )
        }
      />
      {siteId && sectionKey && (
        <div className={s.context}>
          <Icon name="layers" size={15} className={s.contextIcon} />
          <span>{t('page.context', { site: site?.name ?? '…', section: labels.section(sectionKey) })}</span>
          <Link to={paths.site(siteId, 'structure')}>{t('page.context_back')}</Link>
        </div>
      )}
      <div className={s.toolbar}>
        <SearchInput className={s.search} value={query} onChange={setQuery} placeholder={t('page.search')} label={t('page.search')} />
        <Segmented
          label={t('page.filters_label')}
          value={filter}
          onChange={setFilter}
          className={s.filters}
          options={FILTERS.map((f) => ({ value: f, label: t(`filters.${f}`) }))}
        />
        {/* Additive: the design's 233 parametric presets live in the same library (C6) but apart from authored and catalog ones. */}
        {!!demoCount && (
          <Chip variant="accent" selected={demo} icon={demo ? 'check' : 'shapes'} className={s.demoChip} title={t('page.demo_hint')} onClick={() => setDemo((x) => !x)}>
            {t('page.demo', { n: demoCount })}
          </Chip>
        )}
      </div>
      {list.error ? (
        <LoadError error={list.error} onRetry={list.retry} />
      ) : (
        <ThumbPaletteProvider palette={palette} customLabel={t('custom_block')}>
          <div className={s.grid}>
            {list.items.map((p, i) => (
              <PresetCard key={p.id} preset={p} name={labels.name(p)} index={i} addHint={addHint(p)} onFavorite={() => favorite.mutate(p)} onAdd={() => add(p)} />
            ))}
          </div>
          {!list.loading && list.items.length === 0 && <div className={s.empty}>{t('page.empty')}</div>}
          {list.hasMore && (
            <Button variant="secondary" size={38} className={s.more} disabled={list.loadingMore} onClick={list.loadMore}>
              {t('page.more')}
            </Button>
          )}
        </ThumbPaletteProvider>
      )}
      {creating && (
        <NewPresetModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            // design: the new drafts show under «Все»
            setFilter('all');
            setDemo(false);
          }}
        />
      )}
    </>
  );
}
